import { PropsWithChildren, memo, useEffect } from 'react'
import { GrowthBook, GrowthBookProvider } from '@growthbook/growthbook-react'
import { getAnalyticsSessionId } from '@/utils/analytics/session-utils'
import { logger } from '@/utils/logging'
import { AppFeatures } from '../app-features'

interface Props extends PropsWithChildren {
  growthbook: GrowthBook<AppFeatures>
}

const Provider: React.FC<Props> = ({ growthbook, children }) => {
  useEffect(() => {
    growthbook.init().catch((error) => {
      logger().warn('Growthbook Failed to Load Features - Retrying 1 time', { growthbook }, error)
      growthbook.init().catch((error) => {
        logger().error('Growthbook Failed to Load Features on Retry', { growthbook }, error)
      })
    })
  }, [growthbook])

  useEffect(() => {
    // Set the session ID from analytics context on mount and when it changes
    const setSessionId = () => {
      const sessionId = getAnalyticsSessionId()
      if (sessionId) {
        growthbook.setAttributes({
          ...growthbook.getAttributes(),
          session_id: sessionId,
        })
      }
    }

    setSessionId()

    // Listen for analytics state changes (e.g., when session ID is generated or refreshed)
    // The analytics library triggers storage events when context changes
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === '__context' || e.key === null) {
        setSessionId()
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => {
      window.removeEventListener('storage', handleStorageChange)
    }
  }, [growthbook])

  return <GrowthBookProvider growthbook={growthbook}>{children}</GrowthBookProvider>
}

export const ExperimentContextProvider = memo(Provider)
